import type { OfflineScene } from "@novorender/api";

import { log } from "utils/log";

const l = log.miniMap.tiled;

/** Subfolder tiled minimap files live under, inside the SDK's OPFS scene directory. */
const MINIMAP_DIR_NAME = "minimap";

/**
 * Whether one segment of a stored path is one the SDK accepts, mirroring its unexported
 * `isMinimapFileName`. A name it refuses fails the full sync's whole mini map, on every sync.
 */
function isStorableSegment(segment: string): boolean {
    if (segment === "" || segment === "." || segment === ".." || segment.endsWith(".partial")) {
        return false;
    }
    for (let i = 0; i < segment.length; i++) {
        const code = segment.charCodeAt(i);
        if (code <= 0x1f || code === 0x7f || segment[i] === "," || segment[i] === "\\") {
            return false;
        }
    }
    return true;
}

type MinimapOfflineContext = {
    /**
     * Lazy on purpose: incremental caching arms (`OfflineViewState.scene`) after this
     * registry is set up, so the scene may not exist yet at registration time.
     */
    getScene: () => OfflineScene | undefined;
    /** Whether OPFS hits/misses may be teed into new writes this session. */
    writeEnabled: boolean;
    /** Whether OPFS may be read at all (covers fully-synced scenes even when writes are kill-switched). */
    readEnabled: boolean;
};

let context: MinimapOfflineContext | undefined;

/** Arm the tiled minimap route's OPFS read-through. Unregistered, the route is network-only. */
export function registerMinimapOfflineStore(ctx: MinimapOfflineContext): void {
    context = ctx;
}

export function unregisterMinimapOfflineStore(): void {
    context = undefined;
}

/** Test-only: replace/inspect the active registration without importing the effect that normally sets it. */
export function getMinimapOfflineContext(): MinimapOfflineContext | undefined {
    return context;
}

/** Test-only: reset the pending-write backlog between tests. */
export function resetMinimapOfflineTestState(): void {
    pendingWrites.clear();
}

/**
 * Writes that arrived before `context.getScene()` had a scene to write into (on a cold Procore
 * load, mini map fetches can start before the enrolment's `scene()` resolves), keyed by OPFS path
 * so a tile requested again meanwhile queues once. Flushed the next time a request finds a scene:
 * otherwise a file fetched in that window is never written, since `TileBitmapCache` and
 * `fetchPreviewBitmap` keep the decoded bitmap and never fetch that URL again this session.
 * `OfflineScene.requestFile` can't cover it: it needs a scene to be called on.
 */
const pendingWrites = new Map<string, ArrayBuffer>();
/** Bound the backlog in case the scene never appears this session at all. */
const MAX_PENDING_WRITES = 64;

function queuePendingWrite(path: string, buffer: ArrayBuffer): void {
    if (!pendingWrites.has(path) && pendingWrites.size >= MAX_PENDING_WRITES) {
        const oldestPath = pendingWrites.keys().next().value as string;
        pendingWrites.delete(oldestPath);
    }
    pendingWrites.set(path, buffer);
}

/**
 * Flush the backlog through `scene.dir.write`, as the bytes are already in hand. That write is
 * deprecated, and refused in a full sync's directory, where these writes fail and are logged; so
 * is a `QuotaExceededError`, which `requestFile`'s latch then catches on the next tile.
 */
function flushPendingWrites(scene: OfflineScene): void {
    if (pendingWrites.size === 0) {
        return;
    }
    const toFlush = [...pendingWrites];
    pendingWrites.clear();
    for (const [path, buffer] of toFlush) {
        const size = buffer.byteLength;
        void scene.dir.write(path, buffer).then(
            () => scene.reportOfflineFileAccess(path, size, { retain: true }),
            (error: unknown) => l.debug("Failed to flush queued minimap write:", path, error),
        );
    }
}

/**
 * The path a tile-set file is journaled under inside the scene directory, or
 * `undefined` when it can't be stored safely.
 * @remarks
 * Relative paths come from the server's manifest, so they are held to the SDK's rule for these
 * names (see `isStorableSegment`): rejecting costs one uncached file, accepting could cost the
 * full sync's mini map. Not keyed by scene version: re-mapping a sheet mints a new
 * `platformFileInstanceId` rather than regenerating a tile set under the old one.
 */
export function minimapFilePath(fileInstanceId: string, relativePath: string): string | undefined {
    // The instance id is one segment of the path, never several.
    if (!isStorableSegment(fileInstanceId) || fileInstanceId.includes("/")) {
        return undefined;
    }
    if (!relativePath.split("/").every(isStorableSegment)) {
        return undefined;
    }
    return `${MINIMAP_DIR_NAME}/${fileInstanceId}/${relativePath}`;
}

/**
 * OPFS read-through fetch of one tile-set file through `OfflineScene.requestFile`: every tile,
 * preview and manifest request goes through here. A plain network fetch when reading is
 * disabled, the path can't be stored, or there is no scene yet; a write in that last case is
 * queued (see `pendingWrites`).
 * @remarks A write made while reading is disabled queues and never flushes: flushing needs
 * `readEnabled`. No registration disables reading.
 */
export async function requestMinimapFile(
    fileInstanceId: string,
    relativePath: string,
    resolveUrl: () => string | null | Promise<string | null>,
): Promise<Response | undefined> {
    const path = minimapFilePath(fileInstanceId, relativePath);
    const scene = path && context?.readEnabled ? context.getScene() : undefined;

    if (path && scene) {
        flushPendingWrites(scene);
        return scene.requestFile(path, resolveUrl, { retain: true, write: context?.writeEnabled ?? false });
    }

    const url = await resolveUrl();
    if (!url) {
        return undefined;
    }
    const response = await fetch(url);
    if (path && context?.writeEnabled && response.ok) {
        // Scene not armed yet -- queue instead of dropping.
        void response
            .clone()
            .arrayBuffer()
            .then((buffer) => queuePendingWrite(path, buffer))
            .catch((error: unknown) => l.debug("Failed to queue minimap write:", path, error));
    }
    return response;
}

/** Read a tile-set file straight from OPFS (no fetch attempted), or `undefined` on a miss / when reading is unavailable. */
export async function readMinimapFileOffline(
    fileInstanceId: string,
    relativePath: string,
): Promise<ArrayBuffer | undefined> {
    const response = await requestMinimapFile(fileInstanceId, relativePath, () => null);
    return response ? await response.arrayBuffer() : undefined;
}
